import React, { useState } from 'react';
import { format } from 'date-fns';
import { Calendar, Mail, RefreshCw, ExternalLink } from 'lucide-react';
import type { GmailMessage } from '../../types/gmail';
import type { TimelineEntry } from '../../types/timeline';
import TimelineEntryContent from './TimelineEntryContent';
import TimelineActions from './TimelineActions';
import TimelineEntryPopup from './TimelineEntryPopup';

interface UnifiedTimelineProps {
  contactId: string;
  timelineEntries: TimelineEntry[];
  emailMessages: GmailMessage[];
  onUpdate: () => void;
  onRefreshEmails: () => void;
  refreshing?: boolean;
}

type TimelineItem = {
  type: 'manual' | 'email';
  date: Date;
  content: React.ReactNode;
  entry?: TimelineEntry;
};

export default function UnifiedTimeline({ 
  contactId,
  timelineEntries, 
  emailMessages,
  onUpdate,
  onRefreshEmails,
  refreshing = false
}: UnifiedTimelineProps) {
  const [editingEntry, setEditingEntry] = useState<TimelineEntry | null>(null);

  // Combine and sort timeline items
  const timelineItems: TimelineItem[] = [
    ...timelineEntries.map(entry => ({
      type: 'manual' as const,
      date: new Date(entry.date),
      content: (
        <div className="flex justify-between items-start">
          <TimelineEntryContent content={entry.content} />
          <TimelineActions
            entryId={entry.id}
            onEdit={() => setEditingEntry(entry)}
            onDelete={onUpdate}
          />
        </div>
      ),
      entry
    })),
    ...emailMessages.map(message => ({
      type: 'email' as const,
      date: new Date(message.date),
      content: (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="font-medium">{message.subject}</span>
            <a
              href={`https://mail.google.com/mail/u/${encodeURIComponent(message.accountEmail)}/#inbox/${encodeURIComponent(message.id)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary-500 hover:text-primary-400"
              title="Open in Gmail"
            >
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>
          <div className="text-sm text-gray-400">
            {message.from}
          </div>
          <div className="text-sm text-gray-400">
            via {message.accountEmail}
          </div>
          <p className="text-sm text-gray-300 mt-2">
            {message.snippet}
          </p>
        </div>
      )
    }))
  ].sort((a, b) => b.date.getTime() - a.date.getTime());

  return (
    <>
      <div className="space-y-6">
        {timelineItems.length === 0 ? (
          <div className="text-gray-400">No timeline items yet</div>
        ) : (
          timelineItems.map((item, index) => (
            <div key={index} className="relative pl-10">
              <div className="absolute left-0 flex h-8 w-8 items-center justify-center rounded-full bg-dark-700 text-gray-400">
                {item.type === 'manual' ? (
                  <Calendar className="h-4 w-4" />
                ) : (
                  <Mail className="h-4 h-4" />
                )}
              </div>
              
              <div className="card p-4">
                <time className="text-sm font-medium text-gray-400 mb-2 block">
                  {format(item.date, 'MMMM d, yyyy')}
                </time>
                {item.content}
              </div>
            </div>
          ))
        )}
      </div>

      {editingEntry && (
        <TimelineEntryPopup
          contactId={contactId}
          entry={editingEntry}
          onClose={() => setEditingEntry(null)}
          onSuccess={onUpdate}
        />
      )}
    </>
  );
}